"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";

/**
 * LandingCTA — primary call-to-action block for the landing page hero.
 *
 * Signed-out visitors: "Start practicing free" + "Browse exams"
 * Signed-in visitors:  "Go to Dashboard"      + "Browse exams"
 *
 * Designed for the May 2026 cream/indigo redesign — left-aligned, tight
 * pair of buttons, no expanded inline exam grid (the catalog section
 * below the hero handles that).
 */
export function LandingCTA() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setSignedIn(!!user);
    });
  }, []);

  // Loading skeleton (auth check pending)
  if (signedIn === null) {
    return (
      <div className="flex gap-3">
        <div className="h-12 w-48 bg-[var(--border-light)] rounded-lg animate-pulse" />
        <div className="h-12 w-32 bg-[var(--border-light)] rounded-lg animate-pulse" />
      </div>
    );
  }

  if (signedIn) {
    return (
      <div className="flex gap-3 flex-wrap">
        <Link
          href="/dashboard"
          className="bg-[var(--text-primary)] text-[var(--bg-primary)] text-[15px] font-bold px-6 py-3.5 rounded-lg hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
        >
          Go to dashboard →
        </Link>
        <Link
          href="/exams"
          className="bg-white border border-cream text-[var(--text-secondary)] text-[15px] font-medium px-5 py-3.5 rounded-lg hover:border-[var(--text-muted)] transition-colors"
        >
          Browse exams
        </Link>
      </div>
    );
  }

  return (
    <div className="flex gap-3 flex-wrap">
      <Link
        href="/auth?mode=signup"
        className="bg-[var(--text-primary)] text-[var(--bg-primary)] text-[15px] font-bold px-6 py-3.5 rounded-lg hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
      >
        Start practicing free →
      </Link>
      <Link
        href="#exams"
        className="bg-white border border-cream text-[var(--text-secondary)] text-[15px] font-medium px-5 py-3.5 rounded-lg hover:border-[var(--text-muted)] transition-colors"
      >
        Browse exams
      </Link>
    </div>
  );
}
